import './App.sass';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { getEntityType, PoI, PoIType, Entity, Way } from '@the-real-insight/tri-model';
import { MapLayers, MapLayerElement } from './types/MapLayers';

import Map, { Marker, Source, Layer, NavigationControl, ScaleControl, FullscreenControl, GeolocateControl, MapRef } from 'react-map-gl';
import { useAtom } from 'jotai';
import { selectedEntityAtom } from './State';
import markerAddress from './components/map/marker-address-icon.svg';
import markerChargingStation from './components/map/marker-charging-station-icon.svg';
import markerUniversity from './components/map/marker-university-icon.svg';
import markerSchool from './components/map/marker-school-icon.svg';
import markerFireStation from './components/map/marker-fire-station-icon.svg';
import markerBillboard from './components/map/marker-billboard-icon.svg';
import markerChildCareFacility from './components/map/marker-child-care-facility-icon.svg';
import markerHospital from './components/map/marker-hospital-icon.svg';
import markerPharmacy from './components/map/marker-pharmacy-icon.svg';
import markerRestaurant from './components/map/marker-restaurant-icon.svg';
import markerHotel from './components/map/marker-hotel-icon.svg';
import markerAirport from './components/map/marker-airport-icon.svg';
import markerPoliceStation from './components/map/marker-police-station-icon.svg';
import markerFreewayMaintenanceOffice from './components/map/marker-freeway-maintenance-office-icon.svg';
import markerParkingLot from './components/map/marker-parking-lot-icon.svg';
import markerBusStop from './components/map/marker-bus-stop-icon.svg';
import markerChurch from './components/map/marker-church-icon.svg';
import markerHairdresser from './components/map/marker-hairdresser-icon.svg';
import markerSupermarket from './components/map/marker-supermarket-icon.svg';
import { brandMarkerUrl } from './components/map/brandMarkers';
import markerDoctor from './components/map/marker-doctor-icon.svg';
import markerParcelStation from './components/map/marker-parcel-station-icon.svg';
import markerCareFacility from './components/map/marker-care-facility-icon.svg';
import markerCompany from './components/map/marker-company-icon.svg';
import markerTrainStation from './components/map/marker-train-station-icon.svg';
import markerPowerUnit from './components/map/marker-power-unit-icon.svg';
import markerCellularTower from './components/map/marker-cellular-tower-icon.svg';
import { ReactComponent as PollutionSVG } from './components/map/marker-pollution-icon.svg';
import { useTranslation } from 'react-i18next';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MAPBOX_TOKEN } from './utilities/mapbox';
interface Properties {
    setObject?: (object: Entity) => void;
    showMiniMap?: boolean;
    query: string | null;
    data?: any;
    options?: any;
    pois?: any[]
    path?: { lat: number; lon: number }[];
    hideOverlay?: boolean;
}

const mapPoItoSVG = (poiType: any) => {
    switch (poiType) {
        case PoIType.address: return markerAddress;
        case PoIType.chargingStation:
        case 'chargingPoint':
            return markerChargingStation;
        case PoIType.university: return markerUniversity;
        case PoIType.school: return markerSchool;
        case PoIType.fireStation: return markerFireStation;
        case PoIType.advertisementColumn:
        case PoIType.billboard: return markerBillboard;
        case PoIType.childCareFacility: return markerChildCareFacility;
        case PoIType.hospital: return markerHospital;
        case PoIType.pharmacy: return markerPharmacy;
        case PoIType.hotel: return markerHotel;
        case PoIType.restaurant: return markerRestaurant;
        case PoIType.airport: return markerAirport;
        case PoIType.policeStation: return markerPoliceStation;
        case PoIType.freewayMaintenanceOffice: return markerFreewayMaintenanceOffice;
        case PoIType.parkingLot: return markerParkingLot;
        case PoIType.busStop: return markerBusStop;
        case PoIType.church: return markerChurch;
        case PoIType.hairdresser: return markerHairdresser;
        case PoIType.supermarket: return markerSupermarket;
        case PoIType.doctor: return markerDoctor;
        case PoIType.parcelStation: return markerParcelStation
        case PoIType.careFacility: return markerCareFacility
        case PoIType.trainStation: return markerTrainStation;
        case PoIType.powerUnit: return markerPowerUnit;
        case PoIType.cellularTower: return markerCellularTower;
        case 'company': return markerCompany;
        default: return markerSchool;
    }
}


const MapAndImage = forwardRef<any, Properties>((properties, ref) => {
    // MapAndImage props received
    const { t } = useTranslation();
    const [_selectedEntity, setSelectedEntity] = useAtom(selectedEntityAtom) as any;
    const [showSatelliteImage, setShowSatelliteImage] = useState(false);
    const [pois, setPoIs] = useState([]) as any;
    const [ways, setWays] = useState([]) as any;
    const [layerVisibility, setLayerVisibility] = useState<Record<string, boolean>>({});
    const mapRef = useRef<MapRef>(null);
    const hasNewLayers = !!(properties.data?.layers);

    // Initialize/Sync layer visibility from props
    useEffect(() => {
        if (hasNewLayers && properties.data.layers) {
            const initialVisibility: Record<string, boolean> = {};
            Object.entries(properties.data.layers as MapLayers).forEach(([id, layer]) => {
                initialVisibility[id] = layer?.visible !== false;
            });
            setLayerVisibility(initialVisibility);
        }
    }, [hasNewLayers, properties.data?.layers]);

    // Expose map instance to parent component
    useImperativeHandle(ref, () => ({
        getMap: () => mapRef.current?.getMap()
    }), [mapRef]);



    // const calculateHeatmapDetails = (features: any) => {
    //     if (!options.heatmapField || !features || features.length === 0) {
    //         return;
    //     }

    //     let minimum = Number.MAX_VALUE;
    //     let maximum = Number.MIN_VALUE;

    //     features.forEach((feature: any) => {
    //         const value = feature[options.heatmapField];

    //         if (value !== undefined) {
    //             minimum = Math.min(minimum, value);
    //             maximum = Math.max(maximum, value);
    //         }
    //     });

    //     setHeatmapMin(minimum);
    //     setHeatmapMax(maximum);
    // }

    // const adjustBounds = (features: any) => {
    //     let minimumLat = Number.MAX_VALUE;
    //     let maximumLat = Number.MIN_VALUE;
    //     let minimumLon = Number.MAX_VALUE;
    //     let maximumLon = Number.MIN_VALUE;

    //     features.forEach((feature: any) => {
    //         if (feature.boundingBox) {
    //             minimumLat = Math.min(minimumLat, feature.boundingBox[0][0]);
    //             maximumLat = Math.max(maximumLat, feature.boundingBox[1][0]);
    //             minimumLon = Math.min(minimumLon, feature.boundingBox[0][1]);
    //             maximumLon = Math.max(maximumLon, feature.boundingBox[1][1]);
    //         } else if (feature.shape) {
    //             feature.shape.forEach((entry: any) => {
    //                 if (entry.forEach) {
    //                     entry.forEach((point: any) => {
    //                         minimumLat = Math.min(minimumLat, point[0]);
    //                         maximumLat = Math.max(maximumLat, point[0]);
    //                         minimumLon = Math.min(minimumLon, point[1]);
    //                         maximumLon = Math.max(maximumLon, point[1]);
    //                     });
    //                 } else if (entry.lat) {
    //                     minimumLat = Math.min(minimumLat, entry.lat);
    //                     maximumLat = Math.max(maximumLat, entry.lat);
    //                     minimumLon = Math.min(minimumLon, entry.lon);
    //                     maximumLon = Math.max(maximumLon, entry.lon);
    //                 }
    //             });
    //         }
    //     });

    //     setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
    // }

    // useEffect(() => {
    //     const f = async () => {

    //         if (properties.objectClass === 'bridge') {
    //             const newBridges = await userAPI.getBridges(properties.query);

    //             setBridges(newBridges);

    //             newBridges.forEach((bridge: any) => {
    //                 bridge.class = 'Bridge';
    //             });
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, [map, properties.objectClass, properties.query]);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'building') {
    //             let newBuildings;

    //             newBuildings = properties.data.buildings;

    //             setBuildings(newBuildings);

    //             newBuildings.forEach((building: any) => {
    //                 building.class = 'Building';
    //                 // @TODO Temporary
    //                 building.id = building.id.replace('undefined', 'DE')
    //             });

    //             console.log('Buildings >>>', newBuildings);

    //             calculateHeatmapDetails(newBuildings);
    //             adjustBounds(newBuildings);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, [map]);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'Tile') {
    //             let newAreas;

    //             newAreas = properties.data.areas;

    //             // TODO Hack

    //             newAreas.forEach((area: any) => {
    //                 area.class = 'Tile';
    //             });

    //             setAreas(newAreas);
    //             calculateHeatmapDetails(newAreas);
    //             adjustBounds(newAreas);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'LocalGovernment' && properties.data.localGovernments) {
    //             let newLocalGovernments;
    //             newLocalGovernments = properties.data.localGovernments;

    //             // TODO Hack

    //             newLocalGovernments.forEach((area: any) => {
    //                 area.class = 'LocalGovernment';
    //             });

    //             setShapes(newLocalGovernments);
    //             adjustBounds(newLocalGovernments);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'PostalCode' && properties.data.postalCodes) {
    //             let newPostalCodes;
    //             newPostalCodes = properties.data.postalCodes;

    //             // TODO Hack

    //             newPostalCodes.forEach((postalCode: any) => {
    //                 postalCode.class = 'PostalCode';
    //             });

    //             console.log('Postal codes >>>', newPostalCodes);

    //             setShapes(newPostalCodes);
    //             adjustBounds(newPostalCodes);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'PlotOfLand' && properties.data.plotsOfLand) {
    //             let newPlotsOfLand;
    //             newPlotsOfLand = properties.data.plotsOfLand;

    //             // TODO Hacks

    //             newPlotsOfLand.forEach((plotOfLand: any) => {
    //                 plotOfLand.class = 'PlotOfLand';
    //             });

    //             console.log('Plots of land >>>', newPlotsOfLand);

    //             setPlotsOfLand(newPlotsOfLand);
    //             adjustBounds(newPlotsOfLand);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.data.points && properties.options.pieChart) {

    //             console.log('Pie Chart Data ====>>>', properties.data.points);

    //             setPieChartData(properties.data.points)
    //             //adjustBounds(newPlotsOfLand);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     try {
    //         if (map && bounds) {
    //             map.fitBounds(bounds);
    //         }
    //     } catch (error) {
    //         console.error(error);
    //     }
    // }, [map, bounds]);

    useEffect(() => {
        // Properties data received
        if (hasNewLayers) {
            setWays([]);
            setPoIs([]);
            return;
        }

        // TODO Legacy
        if (properties.options?.entityType === 'PoI' && properties.data.map) {
            const newPois = properties.data.map((raw: any) => {
                if (raw._type) {
                    try {
                        const entityType = getEntityType(raw._type);

                        // TODO Hack to keep poiType for LegalPerson

                        const poiType = raw.poiType;

                        raw = new entityType(raw);

                        raw.poiType = poiType;

                        return raw;
                    } catch (e) {
                        return raw;
                    }
                } else {
                    return raw;
                }
            });

            setPoIs(newPois);
        } else {
            if (properties.data.locations) {
                const newPois = properties.data.locations.map((raw: any) => {
                    if (raw._type) {
                        try {
                            const entityType = getEntityType(raw._type);

                            // TODO Hack to keep poiType for LegalPerson

                            const poiType = raw.poiType;

                            raw = new entityType(raw);

                            raw.poiType = poiType;

                            return raw;
                        } catch (e) {
                            return raw;
                        }
                    } else {
                        return raw;
                    }
                });
                setPoIs(newPois);
            }

            if (properties.data.ways) {
                console.log('===> Ways >>>', properties.data.ways.length);

                const newWays = properties.data.ways.map((raw: any) => {
                    if (raw._type) {
                        try {
                            const entityType = getEntityType(raw._type);

                            return new entityType(raw);
                        } catch (e) {
                            return raw;
                        }
                    } else {
                        return raw;
                    }
                });

                setWays(newWays);
            }

            // TODO Shapes
        }

    }, [hasNewLayers, properties?.data, properties?.options]);


    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.getMap();

            const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ways.map((_: Way, i: number) => `path-${i}`) });

                // Reset all paths to default style (purple)
                ways.forEach((_: Way, i: number) => {
                    map.setPaintProperty(`path-${i}`, 'line-width', properties.options?.lineWidth || 10);
                    map.setPaintProperty(`path-${i}`, 'line-color', properties.options?.lineColor || '#EE0000');
                });

                // Highlight the path under the cursor (red)
                if (features.length > 0) {
                    const layerId = features[0].layer.id;
                    const index = parseInt(layerId.split('-')[1]);
                    const _way = ways[index];
                    map.setPaintProperty(layerId, 'line-width', properties.options?.lineWidth || 10);
                    map.setPaintProperty(layerId, 'line-color', '#FF0000');
                }
            };

            const handleClick = (e: mapboxgl.MapMouseEvent) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ways.map((_: Way, i: number) => `path-${i}`) });

                if (features.length > 0) {
                    const layerId = features[0].layer.id;
                    const index = parseInt(layerId.split('-')[1]);
                    const way = ways[index];

                    setSelectedEntity(way);
                }
            };

            map.on('mousemove', handleMouseMove);
            map.on('click', handleClick);

            return () => {
                map.off('mousemove', handleMouseMove);
                map.off('click', handleClick);
            };
        }
    }, [properties.options?.lineColor, properties.options?.lineWidth, setSelectedEntity, ways]);

    // Comprehensive defensive guards after all hooks
    if (!properties) {
        console.error("[MapAndImage] properties is null or undefined");
        return <div>Loading...</div>;
    }

    if (typeof properties !== "object") {
        console.error("[MapAndImage] properties is not an object:", typeof properties, properties);
        return <div>Loading...</div>;
    }

    // Ensure properties.options exists and is an object
    if (!properties.options) {
        console.warn("[MapAndImage] properties.options is null/undefined, using empty object");
        properties.options = {};
    }

    if (typeof properties.options !== "object") {
        console.warn("[MapAndImage] properties.options is not an object, using empty object");
        properties.options = {};
    }

    return <div className="displayFlex flexDirectionColumn gapM">
        <div className="displayFlex gapM">
            <div className="flex items-center gap-2">
                <Switch
                    id="map-satellite-toggle"
                    checked={showSatelliteImage}
                    onCheckedChange={(showSatelliteImage) => setShowSatelliteImage(!!showSatelliteImage)}
                />
                <Label htmlFor="map-satellite-toggle">Show satellite image</Label>
            </div>
        </div>
        <div style={{ position: 'relative' }}>
            <Map mapboxAccessToken={MAPBOX_TOKEN}
                ref={mapRef}
                initialViewState={{
                    longitude: properties.options?.bounds ? undefined : properties.options?.centerLon || 8.670636045048122,
                    latitude: properties.options?.bounds ? undefined : properties.options?.centerLat || 50.10495071520639,
                    bounds: properties.options?.bounds || undefined,
                    zoom: 15
                }}
                style={{ width: '100%', height: 800 }}
                mapStyle={showSatelliteImage ? 'mapbox://styles/mapbox/satellite-streets-v9' : 'mapbox://styles/mapbox/streets-v9'}
                dragPan={true}
                touchZoomRotate={true}
            >
                <NavigationControl position="top-right" />
                <ScaleControl position="bottom-right" />
                <FullscreenControl position="top-right" />
                <GeolocateControl position="top-right" />

                {/* New structured layers */}
                {hasNewLayers && properties.data.layers && Object.entries(properties.data.layers as MapLayers).map(([layerId, layer]) => {
                    if (!layer || !layer.elements || layerVisibility[layerId] === false) return null;

                    return layer.elements.map((element: MapLayerElement, index: number) => {
                    const key = `layer-${layerId}-${index}`;
                    const { display, entity } = element;
                    const targetEntity = entity || element;

                    if (display.type === 'shape' || display.type === 'way') {
                        if (!display.shape) return null;
                        
                        return (
                            <Source key={key} type="geojson" data={{ 
                                type: 'Feature', 
                                properties: {}, 
                                geometry: display.shape 
                            }}>
                                {display.shape.type === 'LineString' ? (
                                    <Layer
                                        id={key}
                                        type="line"
                                        paint={{
                                            'line-color': display.color || '#BB0000',
                                            'line-width': display.weight || 3,
                                            'line-opacity': display.opacity || 0.8
                                        }}
                                    />
                                ) : (
                                    <Layer
                                        id={key}
                                        type="fill"
                                        paint={{
                                            'fill-color': display.fillColor || display.color || '#BB0000',
                                            'fill-opacity': display.fillOpacity || 0.3,
                                            'fill-outline-color': display.color || '#BB0000'
                                        }}
                                    />
                                )}
                            </Source>
                        );
                    } else if (display.type === 'poi') {
                        if (display.lat === undefined || display.lon === undefined) return null;
                        
                        return (
                            <Marker
                                key={key}
                                longitude={display.lon}
                                latitude={display.lat}
                                anchor="center">
                                <div className={`marker`} onClick={() => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }}>
                                    <span className={`pin ${display.poiType}`}>
                                        {display.poiType === 'pollutionData'
                                            ?
                                            <PollutionSVG className="pollutionData" style={{ fill: (targetEntity as any).pfasSum > 100 ? '#FF0000' : ((targetEntity as any).pfasSum > 0 ? '#FFA500' : '#AAAAAA') }} />
                                            :
                                            <img src={brandMarkerUrl((display as any).brandWikidata ?? (entity as any)?.brandWikidata) ?? mapPoItoSVG(display.poiType as string)} alt="" />}
                                    </span>
                                </div>
                            </Marker>
                        );
                    } else if (display.type === 'circle') {
                        if (display.lat === undefined || display.lon === undefined) return null;
                        
                        // Mapbox doesn't have a simple Circle component like Leaflet
                        // We use a GeoJSON point with a circle layer
                        return (
                            <Source key={key} type="geojson" data={{
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'Point',
                                    coordinates: [display.lon, display.lat]
                                }
                            }}>
                                <Layer
                                    id={key}
                                    type="circle"
                                    paint={{
                                        'circle-radius': {
                                            stops: [
                                                [0, 0],
                                                [20, (display.radius || 1000) / 0.075] // rough approximation
                                            ],
                                            base: 2
                                        },
                                        'circle-color': display.fillColor || display.color || '#ff0000',
                                        'circle-opacity': display.fillOpacity || 0.3,
                                        'circle-stroke-color': display.color || '#ff0000',
                                        'circle-stroke-width': display.weight || 2
                                    }}
                                />
                            </Source>
                        );
                    }
                    
                    return null;
                });
            })}

            {/* Legacy layers */}
            {!hasNewLayers && (ways || []).map((way: Way, index: number) => {
                // Skip ways that don't have a shape property or have an empty shape
                if (!way.shape || !Array.isArray(way.shape) || way.shape.length === 0) {
                    return null;
                }
                
                return (
                    <Source
                        key={`path-${index}`}
                        type="geojson"
                        data={{
                            type: 'Feature',
                            properties: {},
                            geometry: {
                                type: 'LineString',
                                coordinates: way.shape.map((point: any) => {
                                    if (Array.isArray(point)) {
                                        return [point[1], point[0]];
                                    } else {
                                        return [point.lon, point.lat]
                                    }
                                })
                            }
                        }}
                    >
                    <Layer
                        id={`path-${index}`}
                        type="line"
                        paint={{
                            'line-color': properties.options?.lineColor || '#BB0000',
                            'line-width': properties.options?.lineWidth || 10,
                            'line-opacity': properties.options?.lineOpacity || 0.8
                        }}
                    />
                </Source>
                );
            })}
            {!hasNewLayers && pois.map((poi: PoI) => <Marker
                longitude={poi.lon}
                latitude={poi.lat}
                anchor="center">
                <div className={`marker`} onClick={() => {
                    if (properties.setObject) properties.setObject(poi);
                    if (!properties.hideOverlay) {
                        setSelectedEntity(poi);
                    }
                }}>
                    <span className={`pin ${poi.poiType}`}>
                        {poi.poiType as string === 'pollutionData'
                            ?
                            <PollutionSVG className="pollutionData" style={{ fill: (poi as any).pfasSum > 100 ? '#FF0000' : ((poi as any).pfasSum > 0 ? '#FFA500' : '#AAAAAA') }} />
                            :
                            <img src={brandMarkerUrl((poi as any).brandWikidata) ?? mapPoItoSVG(poi.poiType)} alt="" />}
                    </span>
                </div>
            </Marker>)}
            </Map>
            {/* Layer selector overlay */}
            {hasNewLayers && properties.data.layers && (
                <div style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    backgroundColor: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 10,
                    minWidth: '150px'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                        {t('map.layers') || 'Layers'}
                    </div>
                    {Object.entries(properties.data.layers as MapLayers).map(([id, layer]) => {
                        if (!layer) return null;
                        const name = layer.name || id.charAt(0).toUpperCase() + id.slice(1);
                        return (
                            <div key={id} className="mb-1.5 flex items-center gap-2">
                                <Checkbox
                                    id={`map-layer-${id}`}
                                    checked={layerVisibility[id] !== false}
                                    onCheckedChange={(checked) => setLayerVisibility(prev => ({ ...prev, [id]: !!checked }))}
                                />
                                <Label htmlFor={`map-layer-${id}`} className="font-normal">{name}</Label>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    </div>;
});

export default MapAndImage;
